import { marked } from 'marked'
import { repairCollapsedReportHeadings, validateCollapsedReportHeadings } from './repairs/collapsed-report-headings'
import { repairPipeTableRows, validatePipeTableRows } from './repairs/pipe-table-rows'
import { repairReportLists, repairReportParagraphs, validateReportLists, validateReportParagraphs } from './repairs/report-blocks'

export const REPORT_MARKDOWN_REPAIRS = Object.freeze([
  'collapsed-report-headings',
  'pipe-table-rows',
  'report-paragraphs',
  'report-lists',
])

const repairs = {
  'collapsed-report-headings': {
    apply: repairCollapsedReportHeadings,
    validate: validateCollapsedReportHeadings,
  },
  'pipe-table-rows': { apply: repairPipeTableRows, validate: validatePipeTableRows },
  'report-paragraphs': { apply: repairReportParagraphs, validate: validateReportParagraphs },
  'report-lists': { apply: repairReportLists, validate: validateReportLists },
}

const presets = { 'flattened-report': REPORT_MARKDOWN_REPAIRS }

const withoutWhitespace = value => value.replace(/\s/gu, '')
const protectedContent = tokens => {
  const values = []
  marked.walkTokens(tokens, token => {
    if (['code', 'codespan', 'html', 'link', 'image'].includes(token.type))
      values.push(token.raw)
  })
  return JSON.stringify(values)
}

/**
 * 默认与 marked.parse 一致。调用方确认输入为 Markdown 后，可指定单个规则 key 或 key 数组。
 * 每条规则先校验自己的适用条件；候选必须保留所有非空白字符，并通过该规则的结果校验才能采用。
 * flattened-report 为组合 key，展开为 REPORT_MARKDOWN_REPAIRS，数组按给定顺序执行。
 * 段落、表格和列表规则仅在本次解析已确认标题坍缩后启用，不独立猜测普通 Markdown。
 * 代码、链接及 HTML 片段的 raw 必须逐项完全不变。
 * HTML 内容应由调用方走原有 HTML 分支；本函数不承担 HTML 清洗。
 */
export function renderMarkdown(source = '', repairKeys = []) {
  const keys = typeof repairKeys === 'string' ? [repairKeys] : repairKeys
  if (!Array.isArray(keys))
    throw new TypeError('Markdown repair keys must be a string or an array')

  const selected = keys.flatMap(key => Object.hasOwn(presets, key) ? presets[key] : [key])
  const appliedKeys = []
  let current = source
  let tokens = marked.lexer(current)
  for (const key of new Set(selected)) {
    const repair = Object.hasOwn(repairs, key) ? repairs[key] : null
    if (!repair)
      throw new RangeError(`Unknown Markdown repair: ${key}`)
    const candidate = repair.apply({ source: current, tokens, appliedKeys })
    if (candidate === current || withoutWhitespace(candidate) !== withoutWhitespace(current))
      continue
    const nextTokens = marked.lexer(candidate)
    if (protectedContent(tokens) !== protectedContent(nextTokens))
      continue
    if (!repair.validate({ beforeTokens: tokens, afterTokens: nextTokens }))
      continue
    appliedKeys.push(key)
    current = candidate
    tokens = nextTokens
  }
  return marked.parser(tokens)
}
